import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getArchivedNotErasedNote } from 'src/pages/BillsPage/archivedAllocationWording';
import { BILL_AUTOMATION_STATUS, POLLABLE_STATUSES } from 'src/stores/billAutomation/uploadedBills';
const TOTAL_AMOUNT_FALLBACK_KEYS = ['totalAmountDue', 'amountExcludingLateFees', 'currentCharges', 'billAmount', 'totalAmount', 'amount'];

/**
 * The amount to allocate for the ONE property utility being worked on: what the bill charges for the
 * utility being allocated, on the property whose allocation is open.
 *
 * Scoped by both, because neither alone names a service. A bill charging two properties for the same
 * utility carries a subtotal for each, so matching on the utility alone returns whichever block the
 * provider happened to print first — the other property's money, every time it is not this one's.
 *
 * Every matching service is added up, not just the first (LB-6015). A bill can print two services that
 * the property keeps as one utility — water and sewer on a "Water/Sewer" utility — and both resolve to
 * it. That utility is allocated once, so taking the first would leave the other charge unbilled.
 *
 * @param {object|null} data Extracted bill payload.
 * @param {{propertyId?: string, utilityId?: string, utilityTypeName?: string}} [scope]
 *   Which service to read. Omit entirely for a whole-bill view, which has no one service to narrow to.
 *
 * Preference order:
 *   1. the services whose resolved `utilityId` matches, within the block for `propertyId`
 *   2. if none does, within that same block the services whose `serviceType` reads as `utilityTypeName`
 *   3. the bill's top-level total, for a payload that resolved no per-service subtotal at all
 * A service without a subtotal adds nothing; when none of the matched ones has one, it is step 3.
 */
export function getExtractedTotalAmount(data) {
  let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!data) return null;
  const propertyId = scope.propertyId,
    utilityId = scope.utilityId,
    utilityTypeName = scope.utilityTypeName;
  const blocks = Array.isArray(data.properties) ? data.properties : [];

  // Blocks a payload never named a property for are kept: on those the single block IS the property,
  // and dropping them would lose the subtotal for every extraction made before ids were stamped.
  const inScope = propertyId ? blocks.filter(block => !(block !== null && block !== void 0 && block.propertyId) || block.propertyId === propertyId) : blocks;
  const services = inScope.flatMap(block => Array.isArray(block === null || block === void 0 ? void 0 : block.services) ? block.services : []);
  const byUtilityId = utilityId ? services.filter(service => (service === null || service === void 0 ? void 0 : service.utilityId) === utilityId) : [];
  const byTypeName = utilityTypeName ? services.filter(service => String((service === null || service === void 0 ? void 0 : service.serviceType) || '').toLowerCase() === String(utilityTypeName).toLowerCase()) : [];

  // A subtotal read as nothing is unknown, not zero (Number(null) is 0), and a sum missing one of its
  // parts is not the utility's charge either: unless every matched service has an amount, the figure is
  // unknown and step 3 stands in, rather than pre-filling 0 or part of the charge.
  const subtotals = (byUtilityId.length ? byUtilityId : byTypeName).map(service => {
    const raw = service === null || service === void 0 ? void 0 : service.subtotal;
    return raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  });
  if (subtotals.length && subtotals.every(Number.isFinite)) {
    // To the cent: adding floats leaves artefacts (10.1 + 20.2 is 30.299999999999997), and this pre-fills a field.
    return subtotals.length === 1 ? subtotals[0] : Math.round(subtotals.reduce((sum, subtotal) => sum + subtotal, 0) * 100) / 100;
  }
  const key = TOTAL_AMOUNT_FALLBACK_KEYS.find(k => data[k] !== undefined && data[k] !== null);
  const fallback = key ? Number(data[key]) : null;
  return Number.isFinite(fallback) ? fallback : null;
}

/**
 * The total the provider is to be paid for the whole bill file, for the AP push.
 *
 * Distinct from {@link getExtractedTotalAmount}, which narrows to the one utility being allocated:
 * when a bill covers several utilities or properties the extractor narrows the top-level totals to
 * the matched slice and records the untouched figures under `invoiceTotals` (with `wasNarrowed`).
 * The amount owed to the provider is the whole invoice, so it is read from there, and only falls
 * back to the top-level total for extractions made before `invoiceTotals` existed.
 */
export function getExtractedInvoiceTotal(data) {
  var _data$invoiceTotals;
  if (!data) return null;
  const candidates = [(_data$invoiceTotals = data.invoiceTotals) === null || _data$invoiceTotals === void 0 ? void 0 : _data$invoiceTotals.totalAmountDue, data.totalAmountDue];
  const found = candidates.find(value => Number.isFinite(Number(value)) && value !== null && value !== '');
  return found === undefined ? null : Number(found);
}

/** Strip leading [ErrorCode] from messages such as "[ServiceProviderNotFoundForProperty] None of the…". */
export function formatBracketedAutomationError(message) {
  const s = String(message).trim();
  const match = s.match(/^\[([^\]]+)]\s*(.+)$/);
  if (match) return match[2].trim();
  return s;
}

/** Flatten + de-duplicate user-facing strings from a list of candidate fields on the response. */
function collectMessageList(candidates) {
  const seen = new Set();
  const result = [];
  for (const c of candidates) {
    if (c == null) continue;
    const arr = Array.isArray(c) ? c : [c];
    for (const entry of arr) {
      let text = '';
      if (typeof entry === 'string') text = formatBracketedAutomationError(entry);else if (entry && typeof entry === 'object' && typeof entry.message === 'string') text = entry.message.trim();else if (entry != null) text = String(entry).trim();
      if (text && !seen.has(text)) {
        seen.add(text);
        result.push(text);
      }
    }
  }
  return result;
}

/**
 * Why the bill stopped where it did, in the service's customer wording (LB-5918): each reason with
 * its next step, as one line. The service words every reason from an allow-list, so none of its
 * stored codes, check details or model notes reach the screen.
 */
function reasonLines(reasons, withNextSteps) {
  return collectMessageList(reasons.map(reason => [reason === null || reason === void 0 ? void 0 : reason.text, withNextSteps ? reason === null || reason === void 0 ? void 0 : reason.nextStep : null].filter(t => typeof t === 'string' && t.trim()).join(' ')));
}

/**
 * Pull human-readable messages from bill automation API error payloads.
 *
 * A service that sends `reasons` (LB-5918) has already said everything a person should read, so the
 * stored errors and the extraction's client-context fields are not read beside it: they are the raw
 * text the reasons were worded from, and a Validated bill's `clientContextValidation` would otherwise
 * count as a blocking error. An empty `reasons` means "nothing to say"; only an absent one, from an
 * older service, falls back to the fields below.
 *
 * `withNextSteps: false` for a screen that doesn't offer the actions the steps name -- they are the
 * Bills page's ("Retry the bill", "Attach it"), and apply billing has its own.
 */
export function getBillAutomationErrorList(response) {
  var _data$clientContextVa;
  let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$withNextSteps = _ref.withNextSteps,
    withNextSteps = _ref$withNextSteps === void 0 ? true : _ref$withNextSteps;
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response.reasons)) return reasonLines(response.reasons, withNextSteps);
  const data = response.data || {};
  return collectMessageList([response.errors, response.errorMessages, data.errors, data.errorMessages, typeof response.error === 'string' ? [response.error] : response.error,
  // Client-context rejections from the bill automation pipeline are blocking, treat them as errors
  data.clientRejectionMessage, data.clientRejectionMessages, (_data$clientContextVa = data.clientContextValidation) === null || _data$clientContextVa === void 0 ? void 0 : _data$clientContextVa.requiredUtilitiesRejectionMessage]);
}

/**
 * Pull non-blocking warning messages from the bill automation API response.
 *
 * `notices` (LB-5918) is the service's customer wording of the warnings, preferred on the same terms
 * as `reasons` above.
 */
export function getBillAutomationWarningList(response) {
  var _data$clientContextVa2;
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response.notices)) return collectMessageList(response.notices);
  const data = response.data || {};
  return collectMessageList([response.warnings, response.warningMessages, data.warnings, data.warningMessages, (_data$clientContextVa2 = data.clientContextValidation) === null || _data$clientContextVa2 === void 0 ? void 0 : _data$clientContextVa2.addressMismatchWarning]);
}

/**
 * Whether the pipeline still has this bill: apply billing keeps asking about it, and shows it as being
 * processed. Pending counts -- a bill that has landed and not been handed on yet moves on its own like
 * the other two, and left out it sat under the spinner until the page was reloaded (LB-5919).
 *
 * The poll and the spinner both read this, so they cannot drift apart again. They had: the poll
 * stopped at anything past Extracted while the spinner stayed up for everything but Validated and
 * Failed, so a bill waiting for a property, or a duplicate, spun for good. With no bill id there is
 * nothing to ask about, so no spinner either.
 */
export function isBillAutomationInFlight(response) {
  return !!(response !== null && response !== void 0 && response.billId) && POLLABLE_STATUSES.includes(response.status);
}

/** The banners apply billing can show above the bill fields. Chosen by {@link getBillAutomationBanner}. */
export const BILL_AUTOMATION_BANNER = {
  /** No bill, or a validated one with nothing extracted to show. */
  none: 'none',
  /** Still in the pipeline: the spinner, for as long as it is being polled. */
  processing: 'processing',
  /** Validated with nothing blocking, so the fields below were filled from it. */
  extracted: 'extracted',
  failed: 'failed',
  /** Read, but it couldn't be used for this property. */
  needsProperty: 'needsProperty',
  /** A copy of a bill already on file. */
  duplicate: 'duplicate',
  /** Rejected: held without being read. */
  held: 'held',
  /** A status this screen doesn't know. */
  unknown: 'unknown'
};

/**
 * Which banner the bill on screen gets. Every status lands on exactly one: the spinner while the bill
 * is in flight, a banner of its own once it has settled.
 *
 * A status this screen doesn't know is settled, not in flight. Nothing here can say whether the
 * pipeline will ever move it, so polling it could go on for as long as the window stays open, with the
 * spinner up the whole time. Its banner says so instead, and points to the Bills page, which knows
 * every status.
 *
 * @param response  the bill as the service last reported it
 * @param hasErrors whether it carries blocking reasons. Only a Validated bill reads it: one that does is
 *                  shown as failed, as it always has been.
 */
export function getBillAutomationBanner(response) {
  let _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref2$hasErrors = _ref2.hasErrors,
    hasErrors = _ref2$hasErrors === void 0 ? false : _ref2$hasErrors;
  if (!response) return BILL_AUTOMATION_BANNER.none;
  if (isBillAutomationInFlight(response)) return BILL_AUTOMATION_BANNER.processing;
  switch (response.status) {
    case BILL_AUTOMATION_STATUS.validated:
      if (hasErrors) return BILL_AUTOMATION_BANNER.failed;
      return response.data ? BILL_AUTOMATION_BANNER.extracted : BILL_AUTOMATION_BANNER.none;
    case BILL_AUTOMATION_STATUS.failed:
      return BILL_AUTOMATION_BANNER.failed;
    case BILL_AUTOMATION_STATUS.needsProperty:
      return BILL_AUTOMATION_BANNER.needsProperty;
    case BILL_AUTOMATION_STATUS.duplicate:
      return BILL_AUTOMATION_BANNER.duplicate;
    case BILL_AUTOMATION_STATUS.rejected:
      return BILL_AUTOMATION_BANNER.held;
    default:
      // A status nobody told this screen about, none at all, or one in flight with no bill id to ask with.
      return BILL_AUTOMATION_BANNER.unknown;
  }
}

/**
 * What the banner says for a bill that settled without filling the fields: a heading, and the lines
 * around its reasons. Worded as the Bills page words the same statuses (LB-5918); the reasons are the
 * service's own, read without their next steps -- see getBillAutomationErrorList.
 *
 * `lead` is always shown under the heading. `reasonsIntro` comes before the reasons when there are
 * any, and `noReasons` stands in for them when there are none. `note` comes last.
 *
 * Null for a banner that isn't one of these: the spinner, the extracted one, or none.
 *
 * @param canRemoveHere whether this screen can delete the bill -- the delete icon on a bill it
 *                      uploaded for this utility, or on the attached file. Only then is that offered
 *                      as a way out, because naming a control that isn't there sends people looking
 *                      for it. Without one the banner carries its own "Upload another bill" button,
 *                      which is the way out it names instead.
 */
export function getSettledBillBannerCopy(banner) {
  let _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref3$canRemoveHere = _ref3.canRemoveHere,
    canRemoveHere = _ref3$canRemoveHere === void 0 ? false : _ref3$canRemoveHere;
  switch (banner) {
    case BILL_AUTOMATION_BANNER.failed:
      return {
        title: 'This bill couldn’t be processed',
        lead: 'Here’s why:',
        noReasons: 'No detailed message was returned. Please confirm the file and property setup, then try again or ' + 'contact support if the issue continues.'
      };
    case BILL_AUTOMATION_BANNER.needsProperty:
      return {
        title: 'This bill couldn’t be used here',
        reasonsIntro: 'We read it, but:',
        note: canRemoveHere ? 'Attach it to its property or retry it on the Bills page. Or remove it with the delete icon and ' + 'upload another bill.' : 'Attach it to its property or retry it on the Bills page, or upload another bill here.'
      };
    case BILL_AUTOMATION_BANNER.duplicate:
      return {
        title: 'This bill is already on file',
        lead: 'It’s a copy of a bill that was uploaded before, so it wasn’t added again.'
      };
    case BILL_AUTOMATION_BANNER.held:
      return {
        title: 'This bill was held for review',
        reasonsIntro: 'We didn’t read it, because:',
        noReasons: 'We didn’t read it.'
      };
    case BILL_AUTOMATION_BANNER.unknown:
      return {
        title: 'We can’t show this bill’s status here',
        lead: 'Check it on the Bills page to see where it stands.'
      };
    default:
      return null;
  }
}

/**
 * The link to a duplicate's original on the Bills page, or null (LB-5924). Only for a company that has the
 * Bills page: without it the link would land on the error page.
 */
export function getOriginalBillLink(response) {
  let _ref4 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref4$hasBillsPage = _ref4.hasBillsPage,
    hasBillsPage = _ref4$hasBillsPage === void 0 ? false : _ref4$hasBillsPage;
  if (!(response !== null && response !== void 0 && response.duplicateOfBillId) || !hasBillsPage) return null;
  return "/bills?billId=".concat(encodeURIComponent(response.duplicateOfBillId));
}

/** Whether the allocation on screen was made from this bill. */
export function isAllocatedFromBill(bill) {
  let _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    allocatedSourceBillId = _ref5.allocatedSourceBillId;
  return !!(bill !== null && bill !== void 0 && bill.billId) && !!allocatedSourceBillId && bill.billId === allocatedSourceBillId;
}

/**
 * Whether apply billing may delete a bill it recovered for the cycle on screen.
 *
 * It may only when this screen is what uploaded it, for this one utility. `uploadedForUtilityId` is
 * what decides that: apply billing is the only channel that sets it, and it holds exactly one
 * utility, so a match means "uploaded here, for what is open now". A bill that arrived by email or
 * through the Bills page has none, and a bill uploaded here for a different utility will not match.
 *
 * The source is checked alongside rather than instead of it, so the two would have to disagree
 * before a bill this screen does not own could be deleted from it.
 *
 * Allocated here is not a reason on its own (LB-5755). Until the allocation is billed a bill can still be
 * deleted, which is how a person corrects a wrong one: bill-automation archives it, and the allocation made
 * from it is archived with it (LB-5952). Once billed -- sent to residents in statements -- it is what they
 * were billed from, and it stays; offering the control then would only ever end in bill-automation's refusal.
 *
 * Nor once the billing month the allocation is in has closed (LB-5952): nothing allocated in a completed cycle is
 * altered, billed or not -- a table left unapproved at the close is locked all the same.
 *
 * @param isBilled whether the allocation on screen is billed: its table's statement has been sent
 * @param isClosed whether the allocation's billing month has closed: its table is no longer editable
 */
export function canDeleteRecoveredBill(bill, utilityId) {
  let _ref6 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    allocatedSourceBillId = _ref6.allocatedSourceBillId,
    _ref6$isBilled = _ref6.isBilled,
    isBilled = _ref6$isBilled === void 0 ? false : _ref6$isBilled,
    _ref6$isClosed = _ref6.isClosed,
    isClosed = _ref6$isClosed === void 0 ? false : _ref6$isClosed;
  if (!(bill !== null && bill !== void 0 && bill.billId) || !utilityId) return false;
  if ((isBilled || isClosed) && isAllocatedFromBill(bill, {
    allocatedSourceBillId
  })) return false;
  return bill.uploadedForUtilityId === utilityId && bill.source === 'Manual';
}
const BILLED_DELETE_REFUSAL = 'This bill has already been billed to residents, so it can no longer be deleted.';

// The words bill-automation refuses it in, for a bill allocated in a month that has closed (LB-5952).
const CLOSED_DELETE_REFUSAL = 'This bill was allocated in a billing month that has closed, so it can no longer be deleted.';

// No "upload another bill" here: an allocation is not moved from one bill to another, and only deleting the bill
// it was made from lets go of it (LB-5952).
const ALLOCATED_ELSEWHERE_FROM_HERE = 'This bill was not uploaded here for this utility, so delete it from the Bills page. ' + 'Its allocation is archived with it.';

/** Why the delete control is disabled, said in terms of what the person can do instead. */
export function getRecoveredBillDeleteHint(canDelete) {
  let _ref7 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref7$isAllocatedHere = _ref7.isAllocatedHere,
    isAllocatedHere = _ref7$isAllocatedHere === void 0 ? false : _ref7$isAllocatedHere,
    _ref7$isBilled = _ref7.isBilled,
    isBilled = _ref7$isBilled === void 0 ? false : _ref7$isBilled,
    _ref7$isClosed = _ref7.isClosed,
    isClosed = _ref7$isClosed === void 0 ? false : _ref7$isClosed;
  if (canDelete) return 'Delete this bill';
  if (isAllocatedHere && isBilled) return BILLED_DELETE_REFUSAL;
  if (isAllocatedHere && isClosed) return CLOSED_DELETE_REFUSAL;
  if (isAllocatedHere) return ALLOCATED_ELSEWHERE_FROM_HERE;
  return 'This bill was not uploaded here for this utility. Delete it from the Bills page.';
}

/**
 * How to introduce a bill this screen did not just receive, as a heading and a sentence.
 *
 * Only a bill uploaded HERE is something the person left unfinished — saying "you left off" about a
 * bill that arrived by email, or one somebody else uploaded on the Bills page, would be telling them
 * about a session they never had. The channel also decides what they can do with it next, so it is
 * named rather than left for them to work out from a disabled control.
 */
export function getRecoveredBillIntro(bill) {
  switch (bill === null || bill === void 0 ? void 0 : bill.source) {
    case 'Manual':
      return {
        title: 'We picked up where you left off',
        body: 'You uploaded this bill here for this utility and closed the screen before finishing, so we ' + 'brought it back rather than asking you to upload it again.'
      };
    case 'Portal':
      return {
        title: 'A bill for this cycle is already on file',
        body: 'It was uploaded on the Bills page and covers this property and utility, so we brought it in ' + 'rather than asking you to upload it again.'
      };
    case 'Email':
      return {
        title: 'A bill for this cycle arrived by email',
        body: 'It covers this property and utility, so we brought it in rather than asking you to upload it ' + 'again.'
      };
    case 'Scrape':
      return {
        title: 'A bill for this cycle was collected for you',
        body: 'It was downloaded from the provider portal and covers this property and utility, so we ' + 'brought it in rather than asking you to upload it again.'
      };
    case 'Integration':
      return {
        title: 'A bill for this cycle came from your provider',
        body: 'It arrived through a provider integration and covers this property and utility, so we ' + 'brought it in rather than asking you to upload it again.'
      };
    default:
      return {
        title: 'A bill for this cycle is already on file',
        body: 'It covers this property and utility, so we brought it in rather than asking you to upload it again.'
      };
  }
}

/** What the person can do about the bill, which is not the same question on every channel. */
export function getRecoveredBillDeleteNote(canDelete) {
  let _ref8 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref8$isAllocatedHere = _ref8.isAllocatedHere,
    isAllocatedHere = _ref8$isAllocatedHere === void 0 ? false : _ref8$isAllocatedHere,
    _ref8$isBilled = _ref8.isBilled,
    isBilled = _ref8$isBilled === void 0 ? false : _ref8$isBilled,
    _ref8$isClosed = _ref8.isClosed,
    isClosed = _ref8$isClosed === void 0 ? false : _ref8$isClosed;
  if (canDelete) return 'If you would rather start over, delete it with the delete icon at the top of this window.';
  if (isAllocatedHere && isBilled) return BILLED_DELETE_REFUSAL;
  if (isAllocatedHere && isClosed) return CLOSED_DELETE_REFUSAL;
  if (isAllocatedHere) return ALLOCATED_ELSEWHERE_FROM_HERE;
  return 'It covers more than this utility, so it cannot be deleted from here — use the Bills page.';
}

/**
 * What deleting the bill does, for the confirmation. Every allocation made from it goes with it (LB-5952): archived,
 * not wiped, so its charges come off and its table is pending again -- this table's and any made from the same bill on
 * another table, which the screen does not show. `allocationCount` is the service's count of them all; without it, only
 * this table's is spoken for.
 */
export function getRecoveredBillDeleteConfirmation() {
  let _ref9 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref9$isAllocatedHere = _ref9.isAllocatedHere,
    isAllocatedHere = _ref9$isAllocatedHere === void 0 ? false : _ref9$isAllocatedHere,
    allocationCount = _ref9.allocationCount;
  const here = isAllocatedHere ? 1 : 0;
  const others = Math.max((allocationCount !== null && allocationCount !== void 0 ? allocationCount : here) - here, 0);
  const removed = 'This bill will be removed from your bills';
  const note = getArchivedNotErasedNote();
  if (here && others) {
    return "".concat(removed, ", and the ").concat(here + others, " allocations made from it archived with it: this table's and ").concat(others, " ") + "other".concat(others === 1 ? '' : 's', ". Their charges come off, and the tables go back to pending, so upload the right ") + "bill to allocate again. ".concat(note);
  }
  if (here) {
    return "".concat(removed, ", and the allocation made from it archived with it. ") + "Its charges come off this table, which goes back to pending, so upload the right bill to allocate again. ".concat(note);
  }
  if (others) {
    return others === 1 ? "".concat(removed, ", and the allocation made from it on another table archived with it. Its charges come off that table, which goes back to pending. ").concat(note) : "".concat(removed, ", and the ").concat(others, " allocations made from it on other tables archived with it. Their charges come off those tables, which go back to pending. ").concat(note);
  }
  return "".concat(removed, ". ").concat(note);
}

/**
 * The toast once the delete is done. No number: the count was read when the delete was asked for, and an allocation
 * made from the bill since then went with it too.
 */
export function getRecoveredBillDeletedToast() {
  let _ref0 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref0$isAllocatedHere = _ref0.isAllocatedHere,
    isAllocatedHere = _ref0$isAllocatedHere === void 0 ? false : _ref0$isAllocatedHere,
    allocationCount = _ref0.allocationCount;
  const total = Math.max(allocationCount !== null && allocationCount !== void 0 ? allocationCount : 0, isAllocatedHere ? 1 : 0);
  if (total > 1) return 'Bill deleted, and the allocations made from it archived with it.';
  return total === 1 ? 'Bill deleted, and its allocation archived with it.' : 'Bill deleted.';
}

/**
 * The extraction narrowed to the one service being allocated: the property whose allocation is open,
 * and within it the utility on screen.
 *
 * The summary and the line items are about THIS allocation, so showing every property the bill covers
 * — and every service on them — asks a person to find their own row in someone else's data. The
 * unnarrowed payload is still what the AP push and the "additional utilities" count read, because
 * those are about the whole invoice.
 */
export function narrowExtractionToScope(data) {
  let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!data) return data;
  const propertyId = scope.propertyId,
    utilityId = scope.utilityId,
    utilityTypeName = scope.utilityTypeName;
  const blocks = Array.isArray(data.properties) ? data.properties : [];
  if (!blocks.length) return data;

  // A block the extraction never named a property for is kept: there the single block IS the property.
  const forProperty = propertyId ? blocks.filter(block => !(block !== null && block !== void 0 && block.propertyId) || block.propertyId === propertyId) : blocks;
  const properties = (forProperty.length ? forProperty : blocks).map(block => {
    const services = Array.isArray(block === null || block === void 0 ? void 0 : block.services) ? block.services : [];
    const matched = services.filter(service => utilityId && service !== null && service !== void 0 && service.utilityId ? service.utilityId === utilityId : utilityTypeName ? String((service === null || service === void 0 ? void 0 : service.serviceType) || '').toLowerCase() === String(utilityTypeName).toLowerCase() : true);

    // Never narrow a block down to nothing: a service nobody resolved a utility for is still the
    // bill's, and hiding it would leave the card blank with no way to tell why.
    return _objectSpread(_objectSpread({}, block), {}, {
      services: matched.length ? matched : services
    });
  });
  return _objectSpread(_objectSpread({}, data), {}, {
    properties
  });
}